import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  filterOptions,
  generateId,
  initialState,
  optionId
} from "@streeteasy/design-system/form/utilities/comboboxUtilities";
import useComboboxHandlers from "@streeteasy/design-system/form/utilities/useComboboxHandlers";
import useSanitizedInput from "@streeteasy/design-system/form/utilities/useSanitizedInput";
import isInViewport from "@streeteasy/design-system/utilities/isInViewport";
import pluralize from "pluralize";
import {
  any,
  arrayOf,
  bool,
  func,
  number,
  object,
  oneOfType,
  string
} from "prop-types";

import * as S from "./styled";

const GroupedCombobox = React.forwardRef(
  (
    {
      className,
      defaultValue,
      groupIdentity,
      id,
      isValid,
      labelIdentity,
      onChange,
      onInputChange,
      onKeyDown,
      onKeyUp,
      options,
      value,
      ...props
    },
    ref
  ) => {
    const listboxId = `${id}-listbox`;
    const [search, setSearch] = useState(
      initialState({ defaultValue, labelIdentity, value })
    );
    const [isVisible, setVisible] = useState(false);
    const keyUpHandler = useSanitizedInput(onKeyUp);

    const optionsRefs = useRef([]);
    const datalistRef = useRef();
    const groupRef = useRef(null);

    optionsRefs.current = [];

    const hideDatalistHandler = e => {
      e.stopPropagation();

      setVisible(false);
      setSearch("");
    };

    const focusHandler = e => {
      props.onFocus(e);
    };

    const blurHandler = e => {
      setVisible(false);
      props.onBlur(e);
    };

    const inputChangeHandler = e => {
      const searchValue = e.target.value;
      setSearch(searchValue);
      setVisible(true);

      const option =
        options.find(o => labelIdentity(o) === searchValue) || null;

      onInputChange(e, searchValue, option);
    };

    const selectHandler = (e, option) => {
      e.preventDefault();

      datalistRef.current.scrollTop = 0;

      setSearch("");
      setVisible(false);
      onChange(option);
    };

    const filteredOptions = useMemo(
      () => filterOptions({ options, identity: labelIdentity, search }),
      [search, options.length]
    );

    const hasResults = useMemo(() => filteredOptions.length > 0, [
      filteredOptions.length
    ]);

    const {
      activeIndex,
      activeItem,
      callback: comboboxHandler,
      previousActiveIndex
    } = useComboboxHandlers({
      onHide: hideDatalistHandler,
      onSelect: selectHandler,
      options: filteredOptions,
      isExpanded: isVisible
    });

    useEffect(() => {
      if (activeIndex > -1) {
        const activeElement = optionsRefs.current[activeIndex];

        if (!isInViewport(activeElement, datalistRef.current)) {
          optionsRefs.current[activeIndex].scrollIntoView({
            block: previousActiveIndex > activeIndex ? "start" : "end",
            inline: "nearest",
            behavior: "auto"
          });
        }
      }
    }, [activeIndex]);

    const keyDownHandler = e => {
      if (isVisible && hasResults) {
        comboboxHandler(e);
      }

      onKeyDown(e);
    };

    return (
      <S.ComboboxWrapper>
        <S.Input
          {...props}
          ref={ref}
          aria-activedescendant={
            activeIndex !== -1 ? optionId(id, activeIndex) : null
          }
          aria-autocomplete="list"
          aria-expanded={isVisible}
          aria-owns={listboxId}
          className={className}
          id={id}
          isValid={isValid}
          onBlur={blurHandler}
          onChange={inputChangeHandler}
          onClick={() => {
            setVisible(true);
          }}
          onFocus={focusHandler}
          onKeyDown={keyDownHandler}
          onKeyUp={keyUpHandler}
          role="combobox"
          value={search}
        />
        {isVisible && (
          <S.InfoBlock isVisuallyHidden={hasResults}>
            {hasResults
              ? `${filteredOptions.length} ${pluralize(
                  "match",
                  filteredOptions.length
                )} found`
              : "No matches found"}
          </S.InfoBlock>
        )}
        {isVisible && (
          <S.SearchStatus aria-atomic aria-live="polite" role="status">
            <span>{`${filteredOptions.length} suggestions`}</span>
          </S.SearchStatus>
        )}
        {hasResults && (
          <S.Datalist
            ref={datalistRef}
            aria-labelledby={props["aria-labelledby"]}
            id={listboxId}
            isExpanded={isVisible}
            role="listbox"
          >
            {filteredOptions.map((option, i) => {
              const group = groupIdentity(option);
              const groupChanged = group !== groupRef.current;
              const groupId = group && generateId(`group-${group}`);

              groupRef.current = groupChanged ? group : groupRef.current;
              groupRef.current =
                i === filteredOptions.length - 1 ? null : groupRef.current;

              return (
                <React.Fragment key={labelIdentity(option)}>
                  {groupChanged && (
                    <S.GroupLabel id={groupId}>{group}</S.GroupLabel>
                  )}
                  <S.Option
                    ref={el => {
                      if (el) {
                        optionsRefs.current.push(el);
                      }
                    }}
                    aria-describedby={groupId}
                    id={optionId(id, i)}
                    isActive={activeItem === option}
                    onMouseDown={e => {
                      selectHandler(e, option);
                    }}
                  >
                    {labelIdentity(option)}
                  </S.Option>
                </React.Fragment>
              );
            })}
          </S.Datalist>
        )}
      </S.ComboboxWrapper>
    );
  }
);

GroupedCombobox.displayName = "Combobox";

GroupedCombobox.propTypes = {
  "aria-labelledby": string,
  className: string,
  defaultValue: oneOfType([string, object, number]),
  groupIdentity: func,
  id: string.isRequired,
  isValid: bool,
  labelIdentity: func,
  onBlur: func,
  onInputChange: func,
  onFocus: func,
  onKeyDown: func,
  onKeyUp: func,
  onChange: func,
  options: arrayOf(any).isRequired,
  value: oneOfType([string, object, number])
};

GroupedCombobox.defaultProps = {
  "aria-labelledby": null,
  className: null,
  defaultValue: null,
  groupIdentity: value => value,
  isValid: true,
  labelIdentity: value => value,
  onBlur: () => {},
  onInputChange: () => {},
  onFocus: () => {},
  onKeyDown: () => {},
  onKeyUp: () => {},
  onChange: () => {},
  value: null
};

export default GroupedCombobox;
